import { Component, Input, OnChanges, OnInit, SimpleChanges, forwardRef } from '@angular/core';
import { ControlValueAccessor, FormBuilder, FormGroup, NG_VALUE_ACCESSOR } from '@angular/forms';
import { CustomModalWithCountrySelectionComponent } from './custom-modal-with-country-selection/custom-modal-with-country-selection.component';
import { ModalController } from '@ionic/angular';
import { countries } from './mock-countries';

@Component({
	selector: 'app-input-phone-with-county-code',
	templateUrl: './input-phone-with-county-code.component.html',
	styleUrls: ['./input-phone-with-county-code.component.scss'],
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => InputPhoneWithCountyCodeComponent),
			multi: true,
		},
	],
})
export class InputPhoneWithCountyCodeComponent implements ControlValueAccessor, OnInit, OnChanges {
	countryCode = '+43';
	flag = 'at';
	@Input() code: string;
	@Input() phone: string;
	@Input() hasBackground: boolean = true;
	onChange: any = () => {};
	onTouched: any = () => {};
	form: FormGroup;

	constructor(
		private fb: FormBuilder,
		private modalCtrl: ModalController
	) {
		this.form = this.fb.group({
			phone: '',
			country: '',
		});
	}

	ngOnInit() {
		this.form.patchValue(
			{
				country: '+43',
			},
			{ emitEvent: false }
		);
		this.form.valueChanges.subscribe(() => {
			const rawPhone = this.form.get('phone')?.value ?? '';
			const phone = rawPhone.replace(/[^\d]/g, '');

			if (rawPhone !== phone) {
				this.form.get('phone')?.setValue(phone, { emitEvent: false });
			}

			this.onChange({
				phone,
				country: this.form.get('country')?.value ?? '',
			});
		});
	}
	ngOnChanges(changes: SimpleChanges): void {}
	writeValue(value: any): void {
		if (value && typeof value === 'object') {
			let phone = String(value.phone ?? '');
			let country = value.country || '+43';

			// Phone stored with a '+' prefix contains the country code inside it.
			// Strip it and derive the correct country code so the flag and selector
			// always reflect the actual number rather than stale stored defaults.
			if (phone.startsWith('+')) {
				if (phone.startsWith(country)) {
					phone = phone.slice(country.length);
				} else {
					// Stored country doesn't match the phone prefix — detect from phone.
					// Sort longest dialCode first to avoid '+1' matching before '+1868'.
					const sorted = [...countries].sort((a, b) => b.dialCode.length - a.dialCode.length);
					const matched = sorted.find(c => phone.startsWith('+' + c.dialCode));
					if (matched) {
						country = '+' + matched.dialCode;
						phone = phone.slice(country.length);
					} else {
						phone = phone.slice(1); // unknown prefix — drop the '+'
					}
				}
			}

			// Sanitize to digits only
			phone = phone.replace(/[^\d]/g, '');

			// Update the flag to match the resolved country code
			const countryData = countries.find(c => '+' + c.dialCode === country);
			if (countryData) {
				this.flag = countryData.flagClass;
			}

			this.form.patchValue({ phone, country }, { emitEvent: false });

			// Propagate the sanitized value to the parent form so its validator
			// sees the correct value and doesn't keep the raw prefilled object.
			// Deferred to avoid calling onChange synchronously inside writeValue.
			setTimeout(() => this.onChange({ phone, country }));
		}
	}

	registerOnChange(fn: any): void {
		this.onChange = fn;
	}

	registerOnTouched(fn: any): void {
		this.onTouched = fn;
	}

	async selectCountryCode() {
		const result = await CustomModalWithCountrySelectionComponent.show(this.modalCtrl, this.form.get('country')?.value);
		console.log(result);
		if (result?.dialCode) {
			this.flag = result.flagClass;
			this.form.patchValue(
				{
					country: '+' + result.dialCode,
				},
				{ emitEvent: true }
			);
		}
	}
}
